import React from 'react';
import { Tag } from 'antd';
import { HtmlContent } from '@client/utils/html_content';
import ItemsWithPopover from '@client/components/ui/table/ItemWithPopover';
import RiskSymbolDisplay from '@client/components/ui/RiskSymbolDisplay';
import TruncatedText from '@client/components/ui/TruncatedText';
import IriisLink from '@client/components/ui/IriisLink';
import { ProductTypeLegend } from '@client/components/ui/productLegends';
import { filterValidTreatmentProducts } from '@client/utils/treatmentValidity';

/**
 * Cell component for displaying CAS (Chemical Abstracts Service) numbers
 *
 * Renders a list of CAS numbers with popover functionality for long lists.
 * Automatically handles overflow and provides clean display.
 *
 * @param {Object} props - Component props
 * @param {string[]} props.casNumbers - Array of CAS number strings
 * @returns {JSX.Element} Formatted CAS numbers cell
 *
 * @example
 * <CasNumbersCell casNumbers={['123-45-6', '789-01-2']} />
 */
export const CasNumbersCell = ({ casNumbers }) => (
  <ItemsWithPopover items={casNumbers} getItemName={(cas) => cas} />
);

/**
 * Cell component for displaying category lists with name properties
 *
 * Generic component for any category-type data that has a 'name' property.
 * Handles multiple categories with consistent formatting.
 *
 * @param {Object} props - Component props
 * @param {Object[]} props.categories - Array of category objects
 * @param {string} props.categories[].name - Category name
 * @returns {JSX.Element} Formatted categories cell
 *
 * @example
 * <CategoriesCell categories={[{name: 'Herbicide'}, {name: 'Fungicide'}]} />
 */
export const CategoriesCell = ({ categories, label = 'name' }) => (
  <ItemsWithPopover items={categories} getItemName={(cat) => cat?.[label]} />
);

/**
 * Cell component for displaying pesticide groups from association data
 *
 * Extracts and displays pesticide group names from complex association objects.
 * Handles nested data structure safely with null checking.
 *
 * @param {Object} props - Component props
 * @param {Object[]} props.associations - Array of pesticide group associations
 * @param {Object} props.associations[].pesticide_group - Pesticide group object
 * @param {string} props.associations[].pesticide_group.name - Group name
 * @returns {JSX.Element} Formatted pesticide groups cell
 *
 * @example
 * <PesticideGroupsCell associations={[{pesticide_group: {name: 'Group A'}}]} />
 */
export const PesticideGroupsCell = ({ associations }) => (
  <ItemsWithPopover
    items={associations}
    getItemName={(association) => association.pesticide_group?.name}
    isNested={true}
  />
);

/**
 * Cell component for displaying chemical families from association data
 *
 * Filters associations to only show those with chemical family data,
 * then displays the family labels in a clean format.
 *
 * @param {Object} props - Component props
 * @param {Object[]} props.associations - Array of chemical family associations
 * @param {Object} props.associations[].chemical_family - Chemical family object
 * @param {string} props.associations[].chemical_family.label - Family label
 * @returns {JSX.Element} Formatted chemical families cell
 *
 * @example
 * <ChemicalFamiliesCell associations={[{chemical_family: {label: 'Organochlorine'}}]} />
 */
export const ChemicalFamiliesCell = ({ associations }) => {
  const familiesWithData =
    associations?.filter((assoc) => assoc.chemical_family) || [];

  return (
    <ItemsWithPopover
      items={familiesWithData}
      getItemName={(association) => association.chemical_family?.label}
    />
  );
};

/**
 * Cell component for displaying resistance development risk information
 *
 * Extracts resistance risk data from nested association structures.
 * Only displays associations that have resistance development data.
 *
 * @param {Object} props - Component props
 * @param {Object[]} props.associations - Array of pesticide associations
 * @param {Object} props.associations[].pesticide_group - Pesticide group object
 * @param {Object} props.associations[].pesticide_group.risk_of_resistance_development - Risk object
 * @param {string} props.associations[].pesticide_group.risk_of_resistance_development.name - Risk name
 * @returns {JSX.Element} Formatted resistance risk cell
 *
 * @example
 * <ResistanceRiskCell associations={[{pesticide_group: {risk_of_resistance_development: {name: 'High'}}}]} />
 */
export const ResistanceRiskCell = ({ associations }) => {
  const risksWithData =
    associations?.filter(
      (assoc) => assoc.pesticide_group?.risk_of_resistance_development
    ) || [];

  return (
    <ItemsWithPopover
      items={risksWithData}
      getItemName={(association) =>
        association.pesticide_group?.risk_of_resistance_development?.name
      }
    />
  );
};

/**
 * Cell component for displaying risk symbols
 *
 * Uses the dedicated RiskSymbolDisplay component to show risk symbols
 * with perfect alignment and consistent styling across the application.
 *
 * @param {Object} props - Component props
 * @param {Object|number} props.symbolData - Symbol data object or legacy integer value
 * @param {string} [props.iconSrc] - Fallback icon source URL (for legacy support)
 * @param {string} [props.alt='Risk symbol'] - Alt text for accessibility
 * @returns {JSX.Element} Risk symbol display
 *
 * @example
 * <RiskSymbolCell symbolData={{ calculated_value: 3, symbol: { image: "/media/symbol_image/3.png", label: "faible" } }} />
 */
export const RiskSymbolCell = ({
  symbolData,
  iconSrc,
  alt = 'Risk symbol',
}) => {
  return (
    <RiskSymbolDisplay
      symbolData={symbolData}
      size="sm"
      fallbackIcon={iconSrc}
      alt={alt}
      hasBackground={false}
    />
  );
};

/**
 * Cell component for rendering HTML content safely
 *
 * Wrapper around HtmlContent utility for consistent HTML rendering in tables.
 * Handles HTML sanitization and formatting automatically.
 *
 * @param {Object} props - Component props
 * @param {string} props.html - HTML string to render
 * @returns {JSX.Element} Safely rendered HTML content
 *
 * @example
 * <HtmlContentCell html="<strong>Substance Name</strong>" />
 */
export const HtmlContentCell = ({ html }) => <HtmlContent html={html} />;

/**
 * Cell component for rendering action links
 *
 * Generic component for creating consistent action links in table cells.
 * Supports customizable href, target, and styling.
 *
 * @param {Object} props - Component props
 * @param {string} props.href - Link destination URL
 * @param {string} [props.target='_blank'] - Link target behavior
 * @param {string} props.className - CSS classes for styling
 * @param {React.ReactNode} props.children - Link content/text
 * @returns {JSX.Element} Formatted action link
 *
 * @example
 * <ActionLinkCell
 *   href="/details/123"
 *   className="btn btn-primary"
 * >
 *   View Details
 * </ActionLinkCell>
 */
export const ActionLinkCell = ({
  href,
  target = '_blank',
  className,
  children,
}) => (
  <a href={href} target={target} className={className} rel="noreferrer">
    {children}
  </a>
);

/**
 * Cell component for displaying collections of items as tags
 *
 * Renders arrays of data as visually organized tag collections.
 * Supports custom key generation and name extraction functions.
 *
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of items to display as tags
 * @param {Function} props.getItemName - Function to extract display name from item
 * @param {Function} [props.getItemKey] - Function to generate unique keys
 * @returns {JSX.Element} Collection of formatted tags
 *
 * @example
 * <TagsCell
 *   items={sectors}
 *   getItemName={(sector) => sector.name}
 *   getItemKey={(sector) => sector.id}
 * />
 */
export const TagsCell = ({ items, getItemName, getItemKey }) => (
  <div className="flex flex-wrap gap-2">
    {items?.map((item, index) => (
      <Tag
        key={getItemKey ? getItemKey(item, index) : `tag-${index}`}
        size="small"
      >
        {getItemName(item)}
      </Tag>
    ))}
  </div>
);

const TreatmentProductItem = ({ treatmentProduct }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <a
      href={`/produits-commerciaux/details/${treatmentProduct?.product?.id}/`}
      target="_blank"
      rel="noreferrer"
      className="text-[#105235] font-roboto text-[12px] font-bold leading-[22px] hover:underline hover:text-[#105235]"
    >
      <TruncatedText text={treatmentProduct?.product?.name} maxLength={30} />
    </a>
    {treatmentProduct?.selection_type === 'series' && (
      <ProductTypeLegend type="serie" />
    )}
    {treatmentProduct?.product?.is_organic_approval && (
      <ProductTypeLegend type="biological" height={20} />
    )}
  </div>
);

const TreatmentProductVisibleContent = ({ items }) => (
  <div className="flex flex-col gap-2">
    {items.map((tp, index) => (
      <React.Fragment key={tp?.product?.id || index}>
        <TreatmentProductItem treatmentProduct={tp} />
        {index < items.length - 1 && (
          <span className="text-[#105235] font-bold text-[13px]">+</span>
        )}
      </React.Fragment>
    ))}
  </div>
);


export const TreatmentProductsCell = ({ TreatmentProducts, skipValidation = false, showPrescription = false }) => {
  // const validProducts = skipValidation
  //   ? TreatmentProducts
  //   : filterValidTreatmentProducts(TreatmentProducts);
  const validProducts = TreatmentProducts || [];

  if (!validProducts?.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  return (
    <div className="flex flex-col gap-2">
      <TreatmentProductVisibleContent items={validProducts} />
      {showPrescription && (
        <Tag color="#FA8C16" className="rounded text-[11px] font-bold w-fit">
          PRESCRIPTION
        </Tag>
      )}
    </div>
  );
};

const SubstanceDisplay = ({ substance }) => {
  const pesticideGroups = substance?.pesticide_group_associations
    ?.map((assoc) => assoc.pesticide_group?.name)
    .filter(Boolean)
    .join(', ');

  return (
    <span className="inline items-center">
      <a
        href={`/matieres-actives/details/${substance?.id}/`}
        target="_blank"
        rel="noreferrer"
        className="text-[#105235] font-roboto text-[12px] font-bold leading-[22px] hover:underline hover:text-[#105235]"
      >
        <HtmlContent html={substance?.name} className="inline" />
      </a>
      {pesticideGroups && (
        <span className="font-roboto text-[12px] font-normal text-[rgba(0,0,0,0.85)] leading-[22px]">
          ({pesticideGroups})
        </span>
      )}
    </span>
  );
};

const ProductSubstancesGroup = ({ treatmentProduct }) => {
  const activeSubstances = treatmentProduct?.product?.active_substances;
  if (!activeSubstances?.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {activeSubstances.map((activeSubstanceProduct, asIndex) => {
        const substance = activeSubstanceProduct?.active_substance;
        if (!substance) return null;

        return (
          <React.Fragment key={substance?.id || asIndex}>
            {asIndex > 0 && (
              <span className="text-[#105235] font-bold text-[12px] mx-0.5">/</span>
            )}
            <SubstanceDisplay substance={substance} />
          </React.Fragment>
        );
      })}
    </div>
  );
};

const ActiveSubstancesVisibleContent = ({ items }) => (
  <div className="flex flex-col gap-2">
    {items.map((tp, index) => (
      <React.Fragment key={tp?.product?.id || index}>
        <ProductSubstancesGroup treatmentProduct={tp} />
        {index < items.length - 1 && (
          <span className="text-[#105235] font-bold text-[12px]">+</span>
        )}
      </React.Fragment>
    ))}
  </div>
);

const ActiveSubstancesPopoverContent = ({ items }) => (
  <div className="flex flex-col max-w-lg">
    {items.map((tp, index) => (
      <div
        key={tp?.product?.id || index}
        className="px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors duration-150"
      >
        <ProductSubstancesGroup treatmentProduct={tp} />
      </div>
    ))}
  </div>
);

export const ActiveSubstancesCell = ({ record, skipValidation = false }) => {
  // const validProducts = skipValidation
  //   ? record?.treatment_products
  //   : filterValidTreatmentProducts(record?.treatment_products);
  const validProducts = record?.treatment_products || [];

  if (!validProducts?.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  const items = validProducts
    .filter((tp) => tp?.product?.active_substances?.length)
    .map((tp) => ({ ...tp, id: tp?.product?.id }));

  if (!items.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  return (
    <ItemsWithPopover
      items={items}
      getItemName={(tp) => tp?.product?.name}
      maxVisible={1}
      CustomVisibleContent={ActiveSubstancesVisibleContent}
      CustomPopoverContent={ActiveSubstancesPopoverContent}
    />
  );
};

const ProductSubstancesVisibleContent = ({ items }) => (
  <div className="flex flex-wrap items-center gap-1">
    {items.map((asp, index) => {
      const substance = asp?.active_substance;
      if (!substance) return null;
      return (
        <React.Fragment key={substance?.id || index}>
          {index > 0 && (
            <span className="text-[#105235] font-bold text-[12px] mx-0.5">/</span>
          )}
          <SubstanceDisplay substance={substance} />
        </React.Fragment>
      );
    })}
  </div>
);

const ProductSubstancesPopoverContent = ({ items }) => (
  <div className="flex flex-col max-w-lg">
    {items.map((asp, index) => {
      const substance = asp?.active_substance;
      if (!substance) return null;
      return (
        <div
          key={substance?.id || index}
          className="px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors duration-150"
        >
          <SubstanceDisplay substance={substance} />
        </div>
      );
    })}
  </div>
);

export const ProductActiveSubstancesCell = ({ activeSubstances }) => {
  if (!activeSubstances?.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  const items = activeSubstances
    .filter((asp) => asp?.active_substance)
    .map((asp) => ({ ...asp, id: asp?.active_substance?.id }));

  if (!items.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  return (
    <ItemsWithPopover
      items={items}
      getItemName={(asp) => asp?.active_substance?.name}
      maxVisible={1}
      CustomVisibleContent={ProductSubstancesVisibleContent}
      CustomPopoverContent={ProductSubstancesPopoverContent}
    />
  );
};

export const CombinedDelayCell = ({ text, separator = '/' }) => {
  if (!text) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  const items = text
    .split(separator)
    .map((v) => v.trim())
    .filter(Boolean)
    .map((v, i) => ({ id: `${i}`, name: v }));

  if (!items.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  return (
    <span className="font-bold text-[#2D5F2B] hover:underline cursor-pointer">
      <ItemsWithPopover
        items={items}
        getItemName={(item) => item.name}
        maxVisible={1}
      />
    </span>
  );
};

export const SimpleTextCell = ({ text }) =>
  text ? (
    <TruncatedText text={text} maxLength={100} />
  ) : (
    <span className="text-gray-400 text-xs italic font-light">N/A</span>
  );

const ProductActivityTypesGroup = ({ treatmentProduct }) => {
  const activityTypesMap = new Map();
  treatmentProduct?.product?.active_substances?.forEach((asp) => {
    asp?.active_substance?.activity_types?.forEach((at) => {
      if (at?.short_name && !activityTypesMap.has(at.id)) {
        activityTypesMap.set(at.id, at);
      }
    });
  });
  const types = Array.from(activityTypesMap.values());
  if (!types.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {types.map((at, i) => (
        <React.Fragment key={at.id}>
          {i > 0 && (
            <span className="text-[#105235] font-bold text-[12px] mx-0.5">/</span>
          )}
          <span className="font-roboto text-[12px] text-[rgba(0,0,0,0.85)] leading-[22px]">
            <HtmlContent html={at.short_name} className="inline" />
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

const ActivityTypesVisibleContent = ({ items }) => (
  <div className="flex flex-col gap-2">
    {items.map((tp, index) => (
      <React.Fragment key={tp?.product?.id || index}>
        <ProductActivityTypesGroup treatmentProduct={tp} />
        {index < items.length - 1 && (
          <span className="text-[#105235] font-bold text-[12px]">+</span>
        )}
      </React.Fragment>
    ))}
  </div>
);

const ActivityTypesPopoverContent = ({ items }) => (
  <div className="flex flex-col max-w-lg">
    {items.map((tp, index) => (
      <div
        key={tp?.product?.id || index}
        className="px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors duration-150"
      >
        <ProductActivityTypesGroup treatmentProduct={tp} />
      </div>
    ))}
  </div>
);

export const ActivityTypesCell = ({ treatmentProducts, skipValidation = false }) => {
  // const validProducts = skipValidation
  //   ? treatmentProducts
  //   : filterValidTreatmentProducts(treatmentProducts);
  const validProducts = treatmentProducts || [];

  if (!validProducts?.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  const items = validProducts
    .filter((tp) =>
      tp?.product?.active_substances?.some(
        (asp) => asp?.active_substance?.activity_types?.length
      )
    )
    .map((tp) => ({ ...tp, id: tp?.product?.id }));

  if (!items.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  return (
    <ItemsWithPopover
      items={items}
      getItemName={(tp) => tp?.product?.name}
      maxVisible={1}
      CustomVisibleContent={ActivityTypesVisibleContent}
      CustomPopoverContent={ActivityTypesPopoverContent}
    />
  );
};

// ========================
// EXTRACTION TABLE CELLS
// ========================

const NA = <span className="text-gray-400 text-xs italic font-light">N/A</span>;

/**
 * Renders "name_doseMin à doseMax unit" per product, "+" between mixture products.
 * Series products show "(série)" suffix.
 */
export const ProductWithDoseCell = ({ treatmentProducts }) => {
  if (!treatmentProducts?.length) return NA;

  const getProductLabel = (tp) => {
    const name = tp?.product?.name || '';
    const isSeries = tp?.selection_type === 'series';
    let doseStr = '';
    if (tp.dose_min != null && tp.dose_max != null && tp.dose_min !== tp.dose_max) {
      doseStr = `${tp.dose_min} à ${tp.dose_max}`;
    } else if (tp.dose_min != null) {
      doseStr = `${tp.dose_min}`;
    } else if (tp.dose_max != null) {
      doseStr = `${tp.dose_max}`;
    }
    const unit = tp.dose_unit?.name || tp.dose_unit || '';
    const dosePart = doseStr ? `_${doseStr}${unit ? ` ${unit}` : ''}` : '';
    return `${name}${isSeries ? ' (série)' : ''}${dosePart}`;
  };

  return (
    <ItemsWithPopover
      items={treatmentProducts.map((tp, i) => ({ ...tp, id: tp?.product?.id || i }))}
      getItemName={getProductLabel}
      maxVisible={1}
    />
  );
};

/**
 * Renders approval/registration numbers, "+" between mixture products.
 */
export const ApprovalNumberCell = ({ treatmentProducts }) => {
  if (!treatmentProducts?.length) return NA;

  const numbers = treatmentProducts
    .map((tp) => tp?.product?.approval_number || '')
    .filter(Boolean);

  if (!numbers.length) return NA;
  return <span className="text-xs">{numbers.join(' + ')}</span>;
};

/**
 * Generic cell for combining a field across products.
 * Uses "/" within same product (multiple substances), "+" between products.
 *
 * @param {Function} extractItems - (treatmentProduct) => string[]
 */
export const CombinedProductFieldCell = ({ treatmentProducts, extractItems }) => {
  if (!treatmentProducts?.length) return NA;

  const parts = treatmentProducts.map((tp) => {
    const items = extractItems(tp);
    return items.filter(Boolean).join(' / ');
  }).filter(Boolean);

  if (!parts.length) return NA;
  return <span className="text-xs">{parts.join(' + ')}</span>;
};

/**
 * Shows approval status name, with "(NOUVEAU)" suffix if approved less than 1 year ago.
 */
export const ApprovalStatusCell = ({ record, nouveauLabel = '(NOUVEAU)' }) => {
  const status = record?.approval_status;
  if (!status?.name) return NA;

  let isNouveau = false;
  if (status.name === 'Actif' && record.approval_date) {
    const approvalDate = new Date(record.approval_date);
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    isNouveau = approvalDate > oneYearAgo;
  }

  return (
    <span className="text-xs">
      {status.name}
      {isNouveau && <span className="text-green-600 font-bold ml-1">{nouveauLabel}</span>}
    </span>
  );
};

const EnemiesVisibleContent = ({ items }) => (
  <div className="flex flex-col gap-1">
    {items.map((enemy, index) => (
      <span key={enemy.id || index}>
        <IriisLink url={enemy.iriisUrl}>
          <TruncatedText text={enemy.name} maxLength={30} />
        </IriisLink>
      </span>
    ))}
  </div>
);

const EnemiesPopoverContent = ({ items }) => (
  <div className="flex flex-col max-w-lg">
    {items.map((enemy, index) => (
      <div
        key={index}
        className="px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors duration-150"
      >
        <div className="font-medium text-sm text-gray-900 leading-relaxed mb-1">
          <IriisLink url={enemy.iriisUrl}>{enemy.name}</IriisLink>
        </div>
        {enemy.rating && (
          <span
            className="inline-block text-xs px-2 py-0.5 rounded-md text-white mt-1"
            style={{ backgroundColor: enemy.ratingColor || '#8C8C8C' }}
          >
            {enemy.rating}
          </span>
        )}
      </div>
    ))}
  </div>
);

export const EnemiesCell = ({ treatmentEnemies, filteredEnemyIds, iriisLinks }) => {
  if (!treatmentEnemies?.length) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  const enemiesMap = new Map();

  treatmentEnemies.forEach((te) => {
    const enemy = te?.enemy;
    if (enemy && !enemiesMap.has(enemy.id)) {
      // Only include enemies that match the filtered IDs
      if (filteredEnemyIds?.length > 0 && !filteredEnemyIds.includes(enemy.id)) {
        return;
      }
      enemiesMap.set(enemy.id, {
        id: enemy.id,
        name: enemy.client_display_name || enemy.french_name || enemy.english_name || enemy.latin_name,
        iriisUrl: iriisLinks?.[enemy.id]?.url,
        rating: te.enemy_rating_ARLA?.name || null,
        ratingColor: te.enemy_rating_ARLA?.display_color || null,
      });
    }
  });

  const enemies = Array.from(enemiesMap.values());

  if (enemies.length === 0) {
    return <span className="text-gray-400 text-xs italic font-light">N/A</span>;
  }

  return (
    <ItemsWithPopover
      items={enemies}
      getItemName={(enemy) => enemy.name}
      maxVisible={1}
      isNested={true}
      CustomVisibleContent={EnemiesVisibleContent}
      CustomPopoverContent={EnemiesPopoverContent}
    />
  );
};
